import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, Bell, Inbox, Banknote, AlertTriangle, FileText, Briefcase, UserCog, CheckCircle2 } from "lucide-react";

const ICONS = {
  cra_validation: Inbox,
  freelance_invoice_validate: FileText,
  freelance_invoice_pay: Banknote,
  mission_validation: Briefcase,
  missions_country: AlertTriangle,
  pending_users: UserCog,
  my_cra: Inbox,
  my_freelance_invoice: FileText,
  cra_late: AlertTriangle,           // Iter-89 Phase 2 — CEO indicator
  cra_exports_pending: FileText,    // Iter-89 Phase 2 — CRA Export queue
};

const COLOR_MAP = {
  amber:   { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-900",   chip: "bg-amber-200 text-amber-900" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900", chip: "bg-emerald-200 text-emerald-900" },
  orange:  { bg: "bg-brand-orange-100/40", border: "border-brand-orange/40", text: "text-brand-orange-600", chip: "bg-brand-orange-100 text-brand-orange-600" },
  red:     { bg: "bg-red-50",     border: "border-red-300",     text: "text-red-900",     chip: "bg-red-200 text-red-900" },
  indigo:  { bg: "bg-indigo-50",  border: "border-indigo-300",  text: "text-indigo-900",  chip: "bg-indigo-200 text-indigo-900" },
};

export default function PendingActionsBlock() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/me/pending-actions")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  if (items.length === 0) {
    return (
      <div className="bg-white border border-emerald-200 rounded-lg p-5 flex items-center gap-3" data-testid="pending-actions-empty">
        <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
        <div className="text-sm text-emerald-900 font-medium">No pending action — you're all set</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-brand-navy/15 rounded-lg p-6" data-testid="pending-actions-block">
      <div className="flex items-center gap-2 mb-4">
        <Bell size={16} className="text-brand-orange" />
        <h2 className="font-heading font-semibold text-base tracking-tight">Pending actions</h2>
        <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 ml-1">{items.length} type(s)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => {
          const Icon = ICONS[it.type] || AlertTriangle;
          const c = COLOR_MAP[it.color] || COLOR_MAP.amber;
          return (
            <Link
              key={it.type}
              to={it.link}
              data-testid={`pending-action-${it.type}`}
              className={`group flex items-start gap-3 p-4 border rounded-lg ${c.bg} ${c.border} ${c.text} hover:-translate-y-0.5 hover:shadow-sm transition-all`}
            >
              <div className={`p-2 rounded-md ${c.chip}`}>
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{it.label}</div>
                <div className="text-[11px] mt-0.5 font-mono opacity-70 inline-flex items-center gap-1">
                  Aller <ArrowUpRight size={11} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
              <span className={`shrink-0 inline-flex items-center justify-center min-w-[26px] h-[26px] px-2 rounded-full text-xs font-bold ${c.chip}`}>
                {it.count > 99 ? "99+" : it.count}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
